import React, { useState } from "react";
import { equipmentCategories } from "../data/mockData";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";

const slugMap = {
  "Scherenbühnen": "scherenbuehnen",
  "Gelenkteleskopbühnen": "gelenkteleskopbuehnen",
  "Teleskopbühnen": "teleskopbuehnen",
  "Spezial-Arbeitsbühnen": "spezial-arbeitsbuehnen",
  "LKW-Arbeitsbühnen": "lkw-arbeitsbuehnen",
  "Teleskopstapler": "teleskopstapler",
  "Gabelstapler": "gabelstapler",
};

const EquipmentCard = ({ item, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const slug = slugMap[item.name] || "scherenbuehnen";

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => navigate(`/geraete/${slug}`)}
      className="group relative bg-[#f5f5f7] rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-2xl hover:shadow-black/5"
    >
      {/* Image */}
      <div className="relative h-72 overflow-hidden bg-gradient-to-b from-[#e8e8ea] to-[#d8d8da]">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      {/* Content */}
      <div className="p-7">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-xl font-semibold text-[#1d1d1f] tracking-tight">
            {item.name}
          </h3>
          <motion.div
            animate={{ x: hovered ? 0 : -5, opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ArrowUpRight size={18} className="text-[#4a7c59]" />
          </motion.div>
        </div>

        <p className="text-sm text-[#6e6e73] leading-relaxed mb-5">
          {item.description}
        </p>

        <div className="flex flex-wrap gap-2">
          {item.specs.map((spec, i) => (
            <span
              key={i}
              className="text-[11px] font-medium text-[#4a7c59] bg-[#4a7c59]/8 px-3 py-1.5 rounded-full"
            >
              {spec}
            </span>
          ))}
        </div>

        {item.useCases && item.useCases.length > 0 && (
          <ul className="mt-5 pt-5 border-t border-[#e5e5e5] space-y-2">
            {item.useCases.map((uc, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[13px] text-[#6e6e73] leading-snug"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#4a7c59] mt-[7px] flex-shrink-0" />
                <span>{uc}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
};

const EquipmentSection = () => {
  const [headerRef, headerInView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section id="equipment" className="py-28 bg-white">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={headerInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-sm font-medium text-[#4a7c59] tracking-widest uppercase mb-3">
            Unser Sortiment
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1d1d1f] tracking-tight mb-5">
            Geräte für jede Höhe.
          </h2>
          <p className="text-lg text-[#6e6e73] max-w-xl mx-auto">
            Von Scherenbühnen bis Teleskopstapler – wir haben das passende Gerät
            für Ihr Projekt.
          </p>
        </motion.div>

        {/* Equipment Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {equipmentCategories.map((item, index) => (
            <EquipmentCard key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default EquipmentSection;
